/*
 * Public "Become a Provider" application.
 * Extended in iter-6 to collect the full broker-credentialing superset
 * (identity, fleet, insurance, docs, owner KYC, banking) so Kaizen / MedexOva
 * / LogistiCare / MTM etc. never reject an MedexOVA TP for missing data.
 *
 * Sections are collapsible — required "core" fields visible up front, deeper
 * credentialing sections expand on demand.
 */
import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { isValidE164, autoPrefixUS } from "../../components/PhoneInput";
import SMSComplianceNotice from "../../components/SMSComplianceNotice";
import { ChevronDown, ChevronRight, Upload, ShieldCheck } from "lucide-react";
import { DUR, EASE, STAGGER } from "../../lib/motion";
import { AnonymizedTicker, BreathingDot } from "../../components/live/LiveWorld";
import PageBackLink from "../../components/PageBackLink";
import { ZonePicker, zipCodesForZoneIds } from "../../components/ZonePicker";
import { useZones } from "../../hooks/useZones";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tpt`;
const INIT = {
  // identity
  company_name: "", dba: "", ein: "", dot_number: "", mc_number: "",
  business_license_number: "", business_license_expiry: "",
  physical_address: "", mailing_address: "", service_area: "", years_in_business: "",
  // structured service area — used by VA dispatch to match trips to nearby providers
  service_area_type: "radius", service_radius_miles: 10, service_home_address: "", service_zone_ids: [], service_zip_codes: "",
  // fleet
  fleet_size: "", vehicle_types: "", wheelchair_lift_count: "", stretcher_count: "",
  // contact
  contact_name: "", contact_phone: "", contact_email: "",
  // insurance
  auto_insurance_carrier: "", auto_insurance_policy_no: "", auto_insurance_effective: "", auto_insurance_expiry: "", auto_coverage_csl: "",
  gl_insurance_carrier: "", gl_insurance_policy_no: "", gl_insurance_expiry: "", gl_coverage_amount: "",
  workers_comp_carrier: "", workers_comp_policy_no: "", workers_comp_expiry: "",
  // owner
  owner_name: "", owner_dob: "", owner_ssn_last4: "", owner_phone: "", owner_email: "",
  // banking
  bank_name: "", bank_routing: "", bank_account_last4: "", bank_account_type: "checking",
  notes: "",
};

function Section({ title, subtitle, defaultOpen = false, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: "1px solid #E9EAE4", borderRadius: 12, marginBottom: 12, background: "#fff" }}>
      <button type="button" onClick={() => setOpen(!open)}
        style={{ width: "100%", background: "none", border: "none", padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", textAlign: "left" }}>
        <div>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#2B4C3B", fontWeight: 700 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>{subtitle}</div>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {badge && <span style={{ fontSize: 10, background: "#F1EFE8", color: "#2B4C3B", padding: "3px 8px", borderRadius: 999, fontWeight: 700 }}>{badge}</span>}
          {open ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
        </div>
      </button>
      {open && <div style={{ padding: "0 18px 18px" }}>{children}</div>}
    </div>
  );
}

export default function PartnerApply() {
  const [form, setForm] = useState(INIT);
  const { zones } = useZones();
  const [docs, setDocs] = useState({ coi: null, w9: null, business_license: null });
  const [uploading, setUploading] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadDoc = async (slot, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Max 15 MB"); return; }
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/coi-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs(d => ({ ...d, [slot]: { doc_id: data.doc_id, filename: data.filename, size: data.size } }));
      toast.success(`${slot.toUpperCase()} uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || `${slot} upload failed`); }
    finally { setUploading(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const contact_phone = autoPrefixUS(form.contact_phone);
    const owner_phone = autoPrefixUS(form.owner_phone);
    if (!isValidE164(contact_phone)) { toast.error("Please enter a 10-digit US phone number for the contact."); return; }
    if (owner_phone && !isValidE164(owner_phone)) { toast.error("Please enter a 10-digit US phone number for the owner."); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        contact_phone,
        owner_phone,
        fleet_size: form.fleet_size ? Number(form.fleet_size) : null,
        years_in_business: form.years_in_business ? Number(form.years_in_business) : null,
        wheelchair_lift_count: form.wheelchair_lift_count ? Number(form.wheelchair_lift_count) : null,
        stretcher_count: form.stretcher_count ? Number(form.stretcher_count) : null,
        auto_coverage_csl: form.auto_coverage_csl ? Number(form.auto_coverage_csl) : null,
        gl_coverage_amount: form.gl_coverage_amount ? Number(form.gl_coverage_amount) : null,
        vehicle_types: form.vehicle_types ? form.vehicle_types.split(",").map(s => s.trim()).filter(Boolean) : [],
        service_zip_codes: Array.from(new Set([
          ...zipCodesForZoneIds(zones, form.service_zone_ids || []),
          ...(form.service_zip_codes ? form.service_zip_codes.split(/[,\s]+/).map(z => z.trim()).filter(z => /^\d{5}$/.test(z)) : []),
        ])),
        service_radius_miles: form.service_radius_miles ? Number(form.service_radius_miles) : 10,
        service_home_address: form.service_home_address || form.physical_address || form.mailing_address || null,
        coi_doc_id: docs.coi?.doc_id || null,
        coi_filename: docs.coi?.filename || null,
        w9_doc_id: docs.w9?.doc_id || null,
        business_license_doc_id: docs.business_license?.doc_id || null,
      };
      delete payload.service_zone_ids;
      await axios.post(`${API}/apply`, payload);
      setSubmitted(true);
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  const F = (label, k, type = "text", extra = {}) => (
    <label style={{ display: "block" }} key={k}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4 }}>{label}</div>
      <input value={form[k]} onChange={(e) => set(k, e.target.value)} type={type} data-testid={`apply-${k}`}
        onBlur={type === "tel" ? () => set(k, autoPrefixUS(form[k])) : undefined}
        style={{ width: "100%", height: 40, border: "1px solid #E2E0D8", borderRadius: 8, padding: "0 12px", fontSize: 14 }} {...extra}/>
    </label>
  );

  const DocUpload = ({ slot, label, hint, required }) => (
    <div style={{ marginBottom: 12, background: "#F9F8F6", border: "1px dashed #D0CDC0", borderRadius: 10, padding: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#0F172A" }}>
        {label}{required && <span style={{ color: "#DC2626", marginLeft: 4 }}>*</span>}
      </div>
      {hint && <div style={{ fontSize: 11, color: "#64748B", marginTop: 3 }}>{hint}</div>}
      <input type="file" accept=".pdf,.png,.jpg,.jpeg" disabled={uploading === slot}
        onChange={(e) => uploadDoc(slot, e.target.files?.[0])}
        data-testid={`apply-doc-${slot}`}
        style={{ display: "block", width: "100%", fontSize: 12, marginTop: 8 }}/>
      {uploading === slot && <div style={{ fontSize: 11, color: "#64748B" }}>Uploading…</div>}
      {docs[slot] && <div style={{ fontSize: 11, color: "#2B4C3B", fontWeight: 600, marginTop: 4 }}>✓ {docs[slot].filename} ({Math.round(docs[slot].size / 1024)} KB)</div>}
    </div>
  );

  if (submitted) return (
    <div style={{ minHeight: "100vh", background: "#F9F8F6", display: "grid", placeItems: "center", padding: 24 }}>
      <div style={{ maxWidth: 480, textAlign: "center", background: "#fff", border: "1px solid #E9EAE4", borderRadius: 14, padding: 40 }} data-testid="apply-submitted">
        <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#16A34A", color: "#fff", display: "grid", placeItems: "center", fontSize: 28, fontWeight: 800, margin: "0 auto 16px" }}>✓</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", marginBottom: 8 }}>Application received</div>
        <div style={{ color: "#64748B", fontSize: 14 }}>Thanks for applying. Our credentialing team will review and reach out within 2 business days.</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#F9F8F6", padding: "40px 20px" }}>
      <PageBackLink fallbackTo="/" label="Back to home" style={{ padding: "0 0 16px", maxWidth: 780 }} />
      <div style={{ maxWidth: 780, margin: "0 auto" }}>
        {/* ── Magic Moment #2 hero ─────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: DUR.narrative, ease: EASE.emerge }}
          style={{
          position: "relative", overflow: "hidden", borderRadius: 20, padding: "40px 32px",
          background: "linear-gradient(135deg, #0F1F17 0%, #2B4C3B 55%, #3A7BD5 130%)",
          color: "#fff", marginBottom: 24, boxShadow: "0 20px 60px rgba(43, 76, 59, 0.35)"
        }}>
          <div aria-hidden style={{ position: "absolute", right: -60, top: -60, width: 260, height: 260, borderRadius: 200, background: "rgba(255,255,255,0.06)" }}/>
          <div aria-hidden style={{ position: "absolute", left: -40, bottom: -80, width: 220, height: 220, borderRadius: 200, background: "rgba(58, 123, 213, 0.15)" }}/>
          <motion.div style={{ position: "relative" }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ staggerChildren: STAGGER.dramatic, delayChildren: 0.2 }}>
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: DUR.reveal, ease: EASE.emerge }}
              style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <BreathingDot color="#10B981" />
              <span style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", opacity: 0.7, fontWeight: 700 }}>MedexOVA · For Providers</span>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: DUR.narrative, ease: EASE.emerge, delay: 0.1 }}
              style={{ fontSize: 34, lineHeight: 1.1, fontWeight: 900, marginTop: 12, letterSpacing: "-0.02em" }}>
              Start your<br/>transportation company.
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 0.86 }}
              transition={{ duration: DUR.narrative, ease: EASE.emerge, delay: 0.24 }}
              style={{ fontSize: 15, marginTop: 14, maxWidth: 520, lineHeight: 1.55 }}>
              Not just another dispatch tool. Tell us who you are, who drives for you, and who your contracts are — we build the operation.
            </motion.div>
            <motion.ul style={{ listStyle: "none", padding: 0, margin: "22px 0 0", display: "grid", gap: 8 }}
              initial="hidden" animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: STAGGER.default, delayChildren: 0.34 } } }}>
              {[
                ["🚐", "Dispatch, POD, and payouts through one portal — no separate billing software"],
                ["🛡️", "Credentialing (Kaizen · MedexOva · MTM · ModivCare) handled once, accepted everywhere"],
                ["📄", "MedexOVA pays your company per Schedule A — load rate, mileage, deadhead, and wait. You own your drivers and their pay."],
                ["⚡", "Bring drivers by CSV or invite. Live in a day."],
              ].map(([e, t]) => (
                <motion.li key={t}
                  variants={{ hidden: { opacity: 0, x: -10 }, show: { opacity: 1, x: 0, transition: { duration: DUR.reveal, ease: EASE.emerge } } }}
                  style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, opacity: 0.94 }}>
                  <span style={{ fontSize: 15 }}>{e}</span>
                  <span>{t}</span>
                </motion.li>
              ))}
            </motion.ul>
            {/* Anonymized live ticker */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ duration: DUR.reveal, delay: 0.6 }}
              style={{ marginTop: 22, padding: "10px 14px", borderRadius: 10, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <BreathingDot color="#10B981"/>
                <div style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "#94A3B8", fontWeight: 800 }}>Live network</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <AnonymizedTicker items={[
                    "A provider in the Midwest went live 8 min ago",
                    "New TP passed MedexOva credentialing this hour",
                    "Schedule A settlement fired · 12 loads booked",
                    "Fleet of 6 added by CSV import · 4 min ago",
                    "A Contender-tier driver joined an MedexOVA TP · 6 min ago",
                  ]}/>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
        {/* ── /Magic Moment #2 hero ────────────────────────────────── */}

        <div style={{ background: "#EEF6EE", border: "1px solid #C6E6C6", borderRadius: 10, padding: 12, marginBottom: 20, display: "flex", gap: 10, alignItems: "flex-start" }}>
          <ShieldCheck size={18} color="#2B4C3B" style={{ flexShrink: 0, marginTop: 2 }}/>
          <div style={{ fontSize: 12, color: "#0F172A" }}>
            <b>Only the fields marked * are required to apply.</b> Insurance, documents, owner and banking details can be added later by our team. Approval typically within one business day. The more you complete now, the faster you go live.
          </div>
        </div>

        <form onSubmit={submit}>
          {/* Section 1: Company & contact (required first) */}
          <Section title="Company & contact" defaultOpen badge="Required">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              {F("DBA / trade name", "dba")}
              {F("EIN", "ein", "text", { placeholder: "XX-XXXXXXX" })}
              {F("Years in business", "years_in_business", "number")}
              {F("Physical address *", "physical_address", "text", { required: true })}
              {F("Mailing address (if different)", "mailing_address")}
              {F("Service area *", "service_area", "text", { required: true, placeholder: "e.g. Metro Detroit" })}
              {F("USDOT #", "dot_number")}
              {F("MC #", "mc_number")}
              {F("Business license #", "business_license_number")}
              {F("Business license expiry", "business_license_expiry", "date")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {F("Primary contact name *", "contact_name", "text", { required: true })}
              {F("Contact phone *", "contact_phone", "tel", { required: true, placeholder: "(940) 555-0100" })}
              {F("Contact email *", "contact_email", "email", { required: true })}
            </div>
          </Section>

          {/* Section 2: Fleet */}
          <Section title="Fleet" defaultOpen badge="Required">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {F("Fleet size (total vehicles) *", "fleet_size", "number", { required: true })}
              {F("Vehicle types (comma-sep)", "vehicle_types", "text", { placeholder: "sedan, wheelchair, stretcher" })}
              {F("# wheelchair-lift vehicles", "wheelchair_lift_count", "number")}
              {F("# stretcher-capable vehicles", "stretcher_count", "number")}
            </div>
          </Section>

          {/* Section 2b: Service area (structured — used by dispatch) */}
          <Section title="Service area (dispatch matching)" defaultOpen badge="Optional" subtitle="How our VA knows which of your drivers to offer a trip. Leave blank = we default to 25 miles from your physical address.">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <label style={{ display: "block" }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4 }}>Service area type</div>
                <select value={form.service_area_type} onChange={(e) => set("service_area_type", e.target.value)}
                  data-testid="tp-apply-service_area_type"
                  style={{ width: "100%", height: 40, border: "1px solid #E2E0D8", borderRadius: 8, padding: "0 12px", fontSize: 14 }}>
                  <option value="radius">Radius from business address</option>
                  <option value="zips">Specific ZIP codes only</option>
                  <option value="both">Both</option>
                </select>
              </label>
              {(form.service_area_type === "radius" || form.service_area_type === "both") && (
                F("Radius (miles) *", "service_radius_miles", "number", { placeholder: "10" })
              )}
            </div>
            {(form.service_area_type === "radius" || form.service_area_type === "both") && (
              F("Radius center (leave blank to use physical address)", "service_home_address", "text", { placeholder: "e.g. 8100 W. 95th St, Chicago IL 60453" })
            )}
            {(form.service_area_type === "zips" || form.service_area_type === "both") && (
              <label style={{ display: "block", marginTop: 12 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4 }}>Zones you serve</div>
                <ZonePicker selectedIds={form.service_zone_ids} onChange={(ids) => set("service_zone_ids", ids)} testId="tp-apply-zone-picker" />
                <details style={{ marginTop: 8 }}>
                  <summary style={{ fontSize: 12, color: "#64748B", cursor: "pointer" }}>Serve a ZIP outside these zones? Add it manually</summary>
                  {F("ZIP codes (comma-separated)", "service_zip_codes", "text", { placeholder: "60620, 60453, 60466" })}
                </details>
              </label>
            )}
          </Section>

          {/* Section 3: Insurance */}
          <Section title="Insurance (auto · GL · workers comp)" badge="Optional — can be added later">
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#94A3B8", margin: "8px 0" }}>Commercial auto</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              {F("Carrier", "auto_insurance_carrier")}
              {F("Policy #", "auto_insurance_policy_no")}
              {F("Effective date", "auto_insurance_effective", "date")}
              {F("Expiry", "auto_insurance_expiry", "date")}
              {F("Combined single limit ($)", "auto_coverage_csl", "number", { placeholder: "1000000" })}
            </div>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#94A3B8", margin: "8px 0" }}>General liability</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              {F("Carrier", "gl_insurance_carrier")}
              {F("Policy #", "gl_insurance_policy_no")}
              {F("Expiry", "gl_insurance_expiry", "date")}
              {F("Coverage ($)", "gl_coverage_amount", "number")}
            </div>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#94A3B8", margin: "8px 0" }}>Workers comp</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {F("Carrier", "workers_comp_carrier")}
              {F("Policy #", "workers_comp_policy_no")}
              {F("Expiry", "workers_comp_expiry", "date")}
            </div>
          </Section>

          {/* Section 4: Documents */}
          <Section title="Documents" badge="Optional — can be added later">
            <DocUpload slot="coi" label="Certificate of Insurance (COI)"
              hint="Naming Medex Transportation Services LLC as certificate holder"/>
            <DocUpload slot="w9" label="Signed W-9"/>
            <DocUpload slot="business_license" label="Business license (copy)"/>
            <div style={{ fontSize: 10, color: "#94A3B8" }}>PDF, JPG, or PNG. Max 15 MB each. Not required to apply — our team can add these after approval.</div>
          </Section>

          {/* Section 5: Owner (KYC) */}
          <Section title="Owner / signing officer (KYC)" subtitle="Required by MedexOva for background verification">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {F("Owner name", "owner_name")}
              {F("DOB", "owner_dob", "date")}
              {F("SSN (last 4)", "owner_ssn_last4", "text", { maxLength: 4, placeholder: "1234" })}
              {F("Owner phone", "owner_phone", "tel", { placeholder: "(940) 555-0100" })}
              {F("Owner email", "owner_email", "email")}
            </div>
          </Section>

          {/* Section 6: Banking */}
          <Section title="Banking (for payouts)" badge="Optional — can be added later" subtitle="ACH direct deposit. Visible only to MedexOVA staff.">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {F("Bank name", "bank_name")}
              {F("Routing #", "bank_routing", "text", { maxLength: 9 })}
              {F("Account # (last 4)", "bank_account_last4", "text", { maxLength: 4 })}
              <label style={{ display: "block" }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4 }}>Account type</div>
                <select value={form.bank_account_type} onChange={(e) => set("bank_account_type", e.target.value)} data-testid="apply-bank_account_type"
                  style={{ width: "100%", height: 40, border: "1px solid #E2E0D8", borderRadius: 8, padding: "0 12px", fontSize: 14 }}>
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </select>
              </label>
            </div>
          </Section>

          {/* Notes */}
          <Section title="Additional notes">
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={4} data-testid="apply-notes"
              style={{ width: "100%", border: "1px solid #E2E0D8", borderRadius: 8, padding: 12, fontSize: 14 }}
              placeholder="Anything else we should know?"/>
          </Section>

          <div style={{ background: "#fff", border: "1px solid #E9EAE4", borderRadius: 12, padding: 20, marginTop: 12 }}>
            <SMSComplianceNotice compact/>
            <button disabled={busy} type="submit" data-testid="apply-submit"
              style={{ width: "100%", height: 50, borderRadius: 10, background: "#2B4C3B", color: "#fff", fontWeight: 700, fontSize: 15, border: "none", cursor: "pointer", marginTop: 12 }}>
              {busy ? "Submitting…" : "Submit application"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
